import axiosInstance from 'services/axiosInstance';
import {
  IAssignTicketPayload,
  IClientPortalChat,
  IClientPortalChatMessage,
  IClientPortalConfiguration,
  IContactTickets,
  ICrmOption,
  ICWContactUpdatePayload,
  IFirstClientPortalChat,
  IGetContactPayload,
  IGetContactRequest,
  IPodData,
  IPodDataSimlified,
  IPsaContact,
  IPushToCWTicketNotePayload,
  ISendMessagePayload,
  ISendOptInPayload,
  ISendPushNotificationPayload,
  ISendTypingActionPayload,
  ISendVerificationCodePayload,
  ITechRequest,
  IValidatePhoneResponsePayload,
  IVerifyCodePayload,
  IVerifyTechPayload,
} from 'services/pod/types';
import { ICommonResponse, IResponse } from 'services/types';
import { IParams } from 'store/pod/thunks/getTechVerificationRequests';

export const getConnectwisePodDataByTicketId = (
  psaId: number,
  ticketId: number,
): IResponse<IPodData> =>
  axiosInstance.request({
    method: 'GET',
    url: `/ConnectwisePod/${psaId}/ticket/${ticketId}`,
  });

export const getConnectwisePodDataByProjectTicketId = (
  psaId: number,
  ticketId: number,
): IResponse<IPodData> =>
  axiosInstance.request({
    method: 'GET',
    url: `/ConnectwisePod/${psaId}/projectTicket/${ticketId}`,
  });

export const getHaloPodDataByTicketId = (psaId: number, ticketId: number): IResponse<IPodData> =>
  axiosInstance.request({
    method: 'GET',
    url: `/HaloPod/${psaId}/ticket/${ticketId}`,
  });

export const getFreshServicePodDataByTicketId = (
  psaId: number,
  ticketId: number,
): IResponse<IPodData> =>
  axiosInstance.request({
    method: 'GET',
    url: `/FreshServicePod/${psaId}/ticket/${ticketId}`,
  });

export const updateConnectwiseContact = (payload: ICWContactUpdatePayload): IResponse<boolean> =>
  axiosInstance.request({
    method: 'POST',
    url: '/ConnectWisePod/saveToContact',
    data: payload,
  });

export const sendVerificationCode = (
  payload: ISendVerificationCodePayload,
): IResponse<ICommonResponse<string>> => {
  const { signal, ...data } = payload;
  return axiosInstance.request({
    method: 'POST',
    url: 'PsaContactValidation/send',
    data,
    signal,
  });
};

export const verifyCode = (payload: IVerifyCodePayload): IResponse<boolean> => {
  const { signal, ...data } = payload;
  return axiosInstance.request({
    method: 'POST',
    url: '/PsaContactValidation/check',
    data,
    signal,
  });
};

export const validatePhoneNumber = (payload: string): IResponse<IValidatePhoneResponsePayload> =>
  axiosInstance.request({
    method: 'POST',
    url: '/MessagingPermissions/check-phone-number',
    data: { phone: payload },
  });

export const getClientPortalChatMessages = (
  chatId: number,
): IResponse<IClientPortalChatMessage[]> =>
  axiosInstance.request({
    method: 'GET',
    url: `/ClientPortalChats/chats/${chatId}/messages`,
  });

export const sendClientPortalChatMessage = (
  payload: ISendMessagePayload,
): IResponse<IClientPortalChatMessage> =>
  axiosInstance.request({
    method: 'POST',
    url: `/ClientPortalChats/chats/${payload.chatId}/messages`,
    data: payload,
  });

export const sendClientPortalChatAttachment = (
  chatId: string | number,
  file: FormData,
  fileType: string,
): IResponse<IClientPortalChatMessage> =>
  axiosInstance.post(`/ClientPortalChats/chats/${chatId}/messages/attachment`, file, {
    headers: {
      'Content-Type': fileType,
    },
  });

export const sendTypingActionToClientPortal = (
  chatId: number,
  payload: ISendTypingActionPayload,
): IResponse<ISendTypingActionPayload> =>
  axiosInstance.request({
    method: 'POST',
    url: `/ClientPortalChats/chats/${chatId}/messages/typing`,
    data: payload,
  });

export const getCrmOptions = (): IResponse<ICrmOption[]> =>
  axiosInstance.request({
    method: 'GET',
    url: '/TicketUpdate/getCrmOptionsDetailed',
  });

export const getContactByPhoneNumber = (
  payload: IGetContactPayload,
): IResponse<IGetContactRequest> =>
  axiosInstance.request({
    method: 'POST',
    url: '/PsaContacts/contacts/checkByPhone',
    data: payload,
  });

export const getContactTickets = (crmId: number, contactId: number): IResponse<IContactTickets[]> =>
  axiosInstance.request({
    method: 'GET',
    url: `/VerifiedPsaTechBySms/crm/${crmId}/tickets/${contactId}`,
  });

export const verifyTechnician = (payload: IVerifyTechPayload): IResponse<any> =>
  axiosInstance.request({ method: 'POST', url: '/VerifiedPsaTechBySms', data: payload });

export const getConnectWisePodDataByContactId = (
  psaId: number,
  contactId: number,
): IResponse<IPodData> =>
  axiosInstance.request({
    method: 'GET',
    url: `/ConnectwisePod/${psaId}/contact/${contactId}`,
  });

export const getAutotaskPodData = (psaId: number, ticketId: string): IResponse<IPodData> =>
  axiosInstance.request({
    method: 'GET',
    url: `/AutotaskPod/${psaId}/ticket/${ticketId}`,
  });

export const getAutotaskPodDataByContactId = (
  psaId: number,
  contactId: number,
): IResponse<IPodDataSimlified> =>
  axiosInstance.request({
    method: 'GET',
    url: `/PsaContacts/autotask/${psaId}/contacts/${contactId}`,
  });

export const getHaloPodDataByContactId = (
  psaId: number,
  contactId: number,
): IResponse<IPodDataSimlified> =>
  axiosInstance.request({
    method: 'GET',
    url: `/PsaContacts/halo/${psaId}/contacts/${contactId}`,
  });

export const getFreshServicePodDataByContactId = (
  psaId: number,
  contactId: number,
): IResponse<IPodDataSimlified> =>
  axiosInstance.request({
    method: 'GET',
    url: `/PsaContacts/freshService/${psaId}/contacts/${contactId}`,
  });

export const getZendeskPodDataContactId = (
  psaId: number,
  contactId: number,
): IResponse<IPodDataSimlified> =>
  axiosInstance.request({
    method: 'GET',
    url: `/PsaContacts/zendesk/${psaId}/contacts/${contactId}`,
  });

export const getServiceNowDataContactId = (
  psaId: number,
  contactId: string,
): IResponse<IPodDataSimlified> =>
  axiosInstance.request({
    method: 'GET',
    url: `/PsaContacts/serviceNow/${psaId}/contacts/${contactId}`,
  });

export const getZendeskPodDataTicketId = (psaId: number, ticketId: number): IResponse<IPodData> =>
  axiosInstance.request({
    method: 'GET',
    url: `/ZendeskPod/${psaId}/ticket/${ticketId}`,
  });

export const getKaseyaPodDataByContactId = (
  psaId: number,
  contactId: number,
): IResponse<IPodDataSimlified> =>
  axiosInstance.request({
    method: 'GET',
    url: `/PsaContacts/kaseya/${psaId}/contacts/${contactId}`,
  });

export const getCommonPodDataByTicketId = (psaId: number, ticketId: string): IResponse<IPodData> =>
  axiosInstance.request({
    method: 'GET',
    url: `/CommonPod/${psaId}/ticket/${ticketId}`,
  });

export const getImportIntegrationPodDataByContactId = (
  psaId: number,
  contactId: number,
): IResponse<IPodDataSimlified> =>
  axiosInstance.request({
    method: 'GET',
    url: `/PsaContacts/importIntegration/${psaId}/contacts/${contactId}`,
  });

export const getSyncroMspPodDataByContactId = (
  psaId: number,
  contactId: number,
): IResponse<IPodDataSimlified> =>
  axiosInstance.request({
    method: 'GET',
    url: `/PsaContacts/syncro/${psaId}/contacts/${contactId}`,
  });

export const getSyncroMspPodDataByTicketId = (
  psaId: number,
  ticketId: number,
): IResponse<IPodData> =>
  axiosInstance.request({
    method: 'GET',
    url: `/SyncroPod/${psaId}/ticket/${ticketId}`,
  });

export const getMicorosoftPodDataByContactId = (
  psaId: number,
  contactId: number,
): IResponse<IPodDataSimlified> =>
  axiosInstance.request({
    method: 'GET',
    url: `/PsaContacts/microsoft/${psaId}/contacts/${contactId}`,
  });

export const getSuperOpsPodDataByContactId = (
  psaId: number,
  contactId: string,
): IResponse<IPodDataSimlified> =>
  axiosInstance.request({
    method: 'GET',
    url: `/PsaContacts/superOps/${psaId}/contacts/${contactId}`,
  });

export const getSuperOpsPodDataByTicketId = (
  psaId: number,
  ticketId: string,
): IResponse<IPodData> =>
  axiosInstance.request({
    method: 'GET',
    url: `/SuperOpsPod/${psaId}/ticket/${ticketId}`,
  });

export const getPsaContactsByCompanyId = (
  psaId: number,
  companyId: number,
  start?: number,
  end?: number,
  searchQuery?: string,
): IResponse<IPsaContact[]> => {
  const params = new URLSearchParams({
    companyId: companyId.toString(),
    alphaSort: 'true',
    _start: (start ?? 0).toString(),
    _end: (end ?? 20).toString(),
  });

  if (searchQuery && searchQuery.trim()) {
    params.append('label', searchQuery.trim());
  }

  return axiosInstance.request({
    method: 'GET',
    url: `/TicketCreate/${psaId}/contacts/v2?${params.toString()}`,
  });
};

export const assignTicketToTechRequest = (payload: IAssignTicketPayload): IResponse<false> =>
  axiosInstance.request({
    method: 'POST',
    url: '/VerifiedPsaTechBySms/link-tech-verification-request',
    data: payload,
  });

export const sendDuoPush = (payload: ISendPushNotificationPayload): IResponse<any> => {
  const { signal, ...data } = payload;
  return axiosInstance.request({
    method: 'POST',
    url: '/PsaContactValidation/sendDuoPush',
    data,
    signal,
  });
};

export const sendMicrosoftPush = (payload: ISendPushNotificationPayload): IResponse<any> => {
  const { signal, ...data } = payload;
  return axiosInstance.request({
    method: 'POST',
    url: '/PsaContactValidation/sendMicrosoftPush',
    data,
    signal,
  });
};

export const pushToTicketNote = (
  psaId: string,
  payload: IPushToCWTicketNotePayload,
): IResponse<boolean> =>
  axiosInstance.request({
    method: 'POST',
    url: `/TicketUpdate/crm/${psaId}/ticket/addNote`,
    data: payload,
  });

export const getTechVerificationRequests = ({
  start,
  end,
  order,
  sort,
  ...rest
}: IParams): IResponse<ITechRequest[]> =>
  axiosInstance.request({
    method: 'GET',
    url: '/VerifiedPsaTechBySms',
    params: {
      _start: start,
      _end: end,
      field: sort,
      order,
      ...rest,
    },
  });

export const getClientPortalConfigurations = (): IResponse<IClientPortalConfiguration[]> =>
  axiosInstance.request({
    method: 'GET',
    url: '/ClientPortal/configurations',
  });

export const getClientPortalChats = (
  psaId: number,
  ticketId: number,
): IResponse<IClientPortalChat[]> =>
  axiosInstance.request({
    method: 'GET',
    url: `/ClientPortalChats/crm/${psaId}/chats?ticketId=${ticketId}`,
  });

export const createClientPortalChat = (
  id: number,
  payload: IFirstClientPortalChat,
): IResponse<IClientPortalChat> =>
  axiosInstance.request({
    method: 'POST',
    url: `/ClientPortalChats/configurations/${id}/chats`,
    data: payload,
  });

export const closeClientPortalChats = (id: number): IResponse<undefined> =>
  axiosInstance.request({
    method: 'DELETE',
    url: `/ClientPortalChats/chats/${id}`,
  });

export const getPodDataByContactId = (
  crmId: number,
  contactId: string,
): IResponse<IPodDataSimlified> =>
  axiosInstance.request({
    method: 'GET',
    url: `/PsaContacts/${crmId}/contacts/${contactId}`,
  });

export const sendOptIn = (payload: ISendOptInPayload): IResponse<boolean> =>
  axiosInstance.request({
    method: 'POST',
    url: '/OptIn/sendOptInUrl',
    data: payload,
  });
